import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { BlogPlatformIntegration } from 'types'

export const getConnectedBlogIntegration = (integrations: ExternalIntegration[]): ExternalIntegration | null => {
  const blogProviders: BlogPlatformIntegration[] = [
    ExternalIntegrationEnum.WordPress,
    ExternalIntegrationEnum.GoHighLevel,
    ExternalIntegrationEnum.Wix,
    ExternalIntegrationEnum.Shopify,
  ]

  // A Zapier integration counts as the workspace's connected blog integration
  // only when it's been explicitly marked as the blog destination via the
  // ZapierTargetsPicker (additional_data.is_blog_destination === true). It
  // takes priority since the workspace can only have ONE blog destination at a
  // time, and toggling Zapier on destroys any prior native blog integration.
  const zapierBlogDestination = integrations.find(
    (integration) =>
      integration.provider === ExternalIntegrationEnum.Zapier &&
      integration.is_active &&
      (integration.additional_data as { is_blog_destination?: boolean } | undefined)?.is_blog_destination === true,
  )
  if (zapierBlogDestination) return zapierBlogDestination

  return (
    integrations.find(
      (integration) => blogProviders.includes(integration.provider as BlogPlatformIntegration) && integration.is_active,
    ) || null
  )
}

export const hasBlogIntegrationConnected = (integrations: ExternalIntegration[]): boolean => {
  return getConnectedBlogIntegration(integrations) !== null
}

export const canConnectBlogProvider = (
  integrations: ExternalIntegration[],
  providerToConnect: BlogPlatformIntegration,
): boolean => {
  const connectedBlogIntegration = getConnectedBlogIntegration(integrations)

  if (!connectedBlogIntegration) {
    return true
  }

  return connectedBlogIntegration.provider === providerToConnect
}

export const getConnectedBlogProviderName = (integrations: ExternalIntegration[]): string | null => {
  const connectedIntegration = getConnectedBlogIntegration(integrations)

  if (!connectedIntegration) {
    return null
  }

  switch (connectedIntegration.provider) {
    case ExternalIntegrationEnum.WordPress:
      return 'WordPress'
    case ExternalIntegrationEnum.GoHighLevel:
      return 'GoHighLevel'
    case ExternalIntegrationEnum.Wix:
      return 'Wix'
    case ExternalIntegrationEnum.Shopify:
      return 'Shopify'
    case ExternalIntegrationEnum.Zapier:
      return 'Zapier'
    default:
      return null
  }
}

export const getBlogIntegrationRestrictionMessage = (
  integrations: ExternalIntegration[],
  attemptedProvider: BlogPlatformIntegration,
): string | null => {
  const connectedProviderName = getConnectedBlogProviderName(integrations)

  if (!connectedProviderName) {
    return null
  }

  let attemptedProviderName: string
  if (attemptedProvider === ExternalIntegrationEnum.WordPress) {
    attemptedProviderName = 'WordPress'
  } else if (attemptedProvider === ExternalIntegrationEnum.GoHighLevel) {
    attemptedProviderName = 'GoHighLevel'
  } else {
    attemptedProviderName = 'Wix'
  }

  if (connectedProviderName === attemptedProviderName) {
    return null
  }

  return `You can connect one blog platform per workspace`
}
